import React from "react";
import { FiEdit2, FiMail, FiPhone } from "react-icons/fi";
import { FaAddressCard } from "react-icons/fa";
import { HiOfficeBuilding, HiUserGroup } from "react-icons/hi";

import AdminButton from "../../_shared/ui/AdminButton";
import AdminDrawer from "../../_shared/ui/AdminDrawer";
import { RoleBadge, VisibleBadge } from "./TrombinoStatusBadges";
import { toAbsolutePhotoUrl } from "../../../../services/trombinosServices";

import {
  formatDate,
  formatDateTime,
  getFullName,
  normalizePhoneForTel,
} from "../utils/trombinosAdminUtils";

const FALLBACK_PHOTO = "/img/no-image.jpg";

export default function TrombinosPreviewDrawer({
  open,
  item,
  onClose,
  onEditFromPreview,
}) {
  const fullName = getFullName(item);
  const photo = item?.photo_url
    ? toAbsolutePhotoUrl(item.photo_url)
    : FALLBACK_PHOTO;

  return (
    <AdminDrawer
      open={open}
      onClose={onClose}
      title="Aperçu du profil"
      subtitle="Visualisez la fiche complète du trombinoscope."
      icon={FaAddressCard}
      size="xl"
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <AdminButton variant="secondary" onClick={onClose}>
            Fermer
          </AdminButton>

          {item?.id ? (
            <AdminButton
              icon={FiEdit2}
              variant="primary"
              onClick={onEditFromPreview}
            >
              Modifier ce profil
            </AdminButton>
          ) : null}
        </div>
      }
    >
      {!item ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Profil introuvable.
        </p>
      ) : (
        <article className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="relative overflow-hidden bg-gradient-to-br from-indigo-700 via-sky-600 to-cyan-600 p-6 text-white sm:p-8">
            <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-white/15 blur-3xl" />

            <div className="relative flex flex-col gap-6 sm:flex-row sm:items-center">
              <div className="h-36 w-36 shrink-0 overflow-hidden rounded-[2rem] border-4 border-white/20 bg-white/15 shadow-xl">
                <img
                  src={photo}
                  alt={fullName || "Profil"}
                  className="h-full w-full object-cover"
                  onError={(event) => {
                    event.currentTarget.src = FALLBACK_PHOTO;
                  }}
                />
              </div>

              <div>
                <div className="flex flex-wrap gap-2">
                  <RoleBadge isElected={!!item.is_elected} />
                  <VisibleBadge visible={!!item.is_visible} />
                </div>

                <h1 className="mt-4 text-3xl font-bold leading-tight sm:text-4xl">
                  {fullName || "Profil sans nom"}
                </h1>

                {item.fonction ? (
                  <p className="mt-3 max-w-3xl text-sm leading-7 text-white/85 sm:text-base">
                    {item.fonction}
                  </p>
                ) : null}
              </div>
            </div>
          </div>

          <div className="grid gap-5 p-5 sm:p-7 lg:grid-cols-[1fr_340px]">
            <div className="space-y-6">
              {item.presentation_courte ? (
                <section className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
                  <h2 className="text-base font-bold text-slate-950 dark:text-white">
                    Présentation courte
                  </h2>

                  <p className="mt-3 whitespace-pre-line text-sm leading-6 text-slate-600 dark:text-slate-300">
                    {item.presentation_courte}
                  </p>
                </section>
              ) : null}

              {item.bio_html ? (
                <section>
                  <h2 className="text-lg font-bold text-slate-950 dark:text-white">
                    Bio détaillée
                  </h2>

                  <div
                    className="prose prose-slate mt-3 max-w-none dark:prose-invert prose-headings:font-bold prose-a:text-sky-600"
                    dangerouslySetInnerHTML={{
                      __html: item.bio_html,
                    }}
                  />
                </section>
              ) : null}

              {!item.presentation_courte && !item.bio_html ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Aucune présentation renseignée.
                </p>
              ) : null}
            </div>

            <aside className="space-y-4">
              <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
                <p className="text-sm font-bold text-slate-950 dark:text-white">
                  Informations
                </p>

                <div className="mt-3 space-y-2 text-sm text-slate-600 dark:text-slate-300">
                  {item.groupe ? (
                    <p className="flex items-center gap-2">
                      <HiUserGroup />
                      {item.groupe}
                    </p>
                  ) : null}

                  {item.service?.nom_service ? (
                    <p className="flex items-center gap-2">
                      <HiOfficeBuilding />
                      {item.service.nom_service}
                    </p>
                  ) : null}

                  {item.role_type ? <p>Type : {item.role_type}</p> : null}

                  <p>Ordre : {item.ordre_affichage ?? 0}</p>

                  <p>Créé le {formatDateTime(item.created_at)}</p>

                  {item.updated_at ? (
                    <p>Mis à jour le {formatDateTime(item.updated_at)}</p>
                  ) : null}
                </div>
              </div>

              <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
                <p className="text-sm font-bold text-slate-950 dark:text-white">
                  Contact
                </p>

                <div className="mt-3 space-y-2 text-sm text-slate-600 dark:text-slate-300">
                  {item.mail_pro ? (
                    <a
                      href={`mailto:${item.mail_pro}`}
                      className="flex items-center gap-2 text-sky-700 underline dark:text-sky-300"
                    >
                      <FiMail />
                      {item.mail_pro}
                    </a>
                  ) : null}

                  {item.tel_pro ? (
                    <a
                      href={`tel:${normalizePhoneForTel(item.tel_pro)}`}
                      className="flex items-center gap-2 underline"
                    >
                      <FiPhone />
                      {item.tel_pro}
                    </a>
                  ) : null}

                  {!item.mail_pro && !item.tel_pro ? (
                    <p>Aucun contact renseigné.</p>
                  ) : null}
                </div>
              </div>

              {(item.mandat_debut || item.mandat_fin) && (
                <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
                  <p className="text-sm font-bold text-slate-950 dark:text-white">
                    Mandat
                  </p>

                  <div className="mt-3 text-sm text-slate-600 dark:text-slate-300">
                    <p>
                      {item.mandat_debut ? formatDate(item.mandat_debut) : "—"}
                      {item.mandat_fin
                        ? ` → ${formatDate(item.mandat_fin)}`
                        : ""}
                    </p>
                  </div>
                </div>
              )}
            </aside>
          </div>
        </article>
      )}
    </AdminDrawer>
  );
}
